import { Button, ButtonTheme } from 'components/Button';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { TextField } from 'components/Field/TextField';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useConfirm, useConfirmUnsaved } from 'hooks/useConfirm';
import { useSelector } from 'hooks/useSelector';
import { filterArrayEmpty, randomKey } from 'lib/riskbase';
import { pluralise } from 'lib/pluralise';
import { cloneDeep, difference, intersection, isEqual, orderBy } from 'lodash';
import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  doFieldSettingsSectionSaved,
  doFieldSettingsSetSectionId,
  doFieldSettingsSectionDelete,
  doFieldSettingsDeleteCustomer,
  doFieldSettingsSectionSave,
  doFieldSettingUnlinkCustomer,
  doFieldSettingsSetGroupId,
} from 'store/actions';
import {
  eventReorderScreenDone,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
} from 'store/app/actions';
import {
  selectOrganisationFieldSettings,
  selectFieldSettingsSectionId,
  selectRemoteOrganisationId,
  selectOrganisationFieldSettingsResultCount,
} from 'store/remote/selectors';
import { useEsc } from 'hooks/useEsc';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { theme } from 'lib/theme';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { useAlert } from 'hooks/useAlert';
import { View } from 'react-native';
import { FieldTypeKey } from 'types/types';
import { FieldSettingsSection } from 'store/remote/types';
import { Txt } from 'components/Txt';

type NavigationRowGroup = {
  label: string;
  label2?: string;
  id: string;
  value: string;
  identifier: string;
};

type State = {
  section: FieldSettingsSection;
};

/**
 * @todo
 * Update state when a new group is added to section
 */

export const FieldSettingsSectionModal: React.FC<
  ModalScreenProps<ModalScreens.FieldSettingsSection>
> = ({ navigation }) => {
  const sectionId = useSelector(selectFieldSettingsSectionId);
  const confirm = useConfirm();
  const confirmUnsaved = useConfirmUnsaved();
  const alert = useAlert();
  const dispatch = useDispatch();
  const forceUpdate = useForceUpdate();

  const organisationId = useSelector(selectRemoteOrganisationId)!;
  const fieldSettings = useSelector(selectOrganisationFieldSettings);
  const resultsCount = useSelector(selectOrganisationFieldSettingsResultCount);

  const section = useMemo<FieldSettingsSection>(
    () =>
      (sectionId ? fieldSettings.sections[sectionId] : undefined) || {
        groupIds: [],
        id: '',
        managedByOrganisation: undefined,
        name: '',
        usedByOrganisations: [],
      },
    [fieldSettings.sections, sectionId],
  );
  const editable = fieldSettings.editable && !section.managedByOrganisation;

  const afterSaveRef = React.useRef<
    ((savedSectionId: string) => void) | undefined
  >(undefined);
  const dataRef = React.useRef(cloneDeep(section));

  const [resetDataI, setResetDataI] = React.useState(0);
  const resetDataIRef = React.useRef(resetDataI);
  if (resetDataIRef.current !== resetDataI) {
    dataRef.current = cloneDeep(section);
    resetDataIRef.current = resetDataI;
  } else {
    // Add / remove groups from ref when they're added / deleted from state
    const newGroupIds = difference(section.groupIds, dataRef.current.groupIds);
    if (newGroupIds.length) {
      dataRef.current.groupIds.push(...newGroupIds);
    }
    dataRef.current.groupIds = intersection(
      dataRef.current.groupIds,
      section.groupIds,
    );
  }

  const checkIsDirty = React.useCallback(
    () => !isEqual(section, dataRef.current),
    [section],
  );
  const isDirty = React.useRef(false);
  isDirty.current = checkIsDirty();
  const isDirtyVar = isDirty.current;

  const [showOrganisationUsage, setShowOrganisationUsage] = React.useState(
    section.usedByOrganisations.length < 25,
  );

  const handleDirtyUpdate = React.useCallback(() => {
    if (isDirty.current !== checkIsDirty()) {
      forceUpdate();
    }
  }, [checkIsDirty, forceUpdate]);

  /** EDIT */

  const canSave = (): boolean => !(!sectionId && !dataRef.current.name);
  const showCannotSaveAlert = (): void => {
    alert({ text: 'Please enter a section name' });
  };

  const handleChange = <T extends keyof State['section']>(
    name: T,
    value: State['section'][T],
  ) => {
    dataRef.current[name] = value;
    handleDirtyUpdate();
  };

  const handleSave = (afterSave: (savedSectionId: string) => void) => {
    if (!canSave()) {
      showCannotSaveAlert();
      return;
    }
    if (!sectionId || isDirty.current) {
      afterSaveRef.current = afterSave;
      dispatch(
        sectionId
          ? doFieldSettingsSectionSave({
              id: sectionId,
              data: {
                groupIds: dataRef.current.groupIds,
                name: dataRef.current.name,
              },
            })
          : doFieldSettingsSectionSave({
              id: undefined,
              data: {
                name: dataRef.current.name,
              },
            }),
      );
      return;
    }
    afterSave(sectionId);
  };

  const handleDelete = () =>
    confirm({
      destructive: true,
      confirmText: 'Delete',
      title: 'Delete Section',
      text: `All groups, fields, and results will be deleted${pluralise(
        section.usedByOrganisations.length,
        '\n\nAnother organisation shares this section; continuing will delete it for them as well. ' +
          'Unlink them first if you want them to continue having access',
        '\n\n{x} other organisations share this section; continuing will delete it for them as well. ' +
          'Unlink them first if you want them to continue having access',
        '',
      )}\n\nThis cannot be undone`,
      onConfirm: () =>
        dispatch(
          doFieldSettingsSectionDelete({
            id: sectionId!,
          }),
        ),
    });

  /** GROUPS */

  const handleAddGroup = (repeatable: boolean) => {
    if (!sectionId) {
      handleSave(() => {
        dispatch(doFieldSettingsSetGroupId(undefined));
        dispatch(
          NAVIGATE_MODAL({
            screen: ModalScreens.FieldSettingsGroup,
            params: {
              newData: {
                repeatable,
              },
            },
          }),
        );
      });
      return;
    }
    dispatch(doFieldSettingsSetGroupId(undefined));
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.FieldSettingsGroup,
        params: {
          newData: {
            repeatable,
          },
        },
      }),
    );
  };

  const handlePressGroup = React.useCallback(
    (groupId: string) => () => {
      dispatch(doFieldSettingsSetGroupId(groupId));
      dispatch(
        NAVIGATE_MODAL({
          screen: ModalScreens.FieldSettingsGroup,
          params: {},
        }),
      );
    },
    [dispatch],
  );

  const { groupIds } = dataRef.current;
  const navigationRowGroups = React.useMemo<NavigationRowGroup[]>(() => {
    return groupIds
      .map((groupId) => {
        const group = fieldSettings.groups[groupId];
        if (!group) {
          return null;
        }
        const { countFields, countResults } = group.fieldIds.reduce(
          (acc, fieldId) => {
            const field = fieldSettings.fields[fieldId];
            if (!field) {
              return acc;
            }
            acc.countFields += 1;
            if (field.type === FieldTypeKey.grouplight && field.childGroupId) {
              acc.countResults += (
                fieldSettings.groups[field.childGroupId]?.fieldIds || []
              ).reduce(
                (acc1, childFieldId) =>
                  acc1 +
                  Object.values(resultsCount?.[childFieldId] || {}).reduce(
                    (sum, count) => sum + count,
                    0,
                  ),
                0,
              );
            } else {
              acc.countResults += Object.values(
                resultsCount?.[fieldId] || {},
              ).reduce((sum, count) => sum + count, 0);
            }
            return acc;
          },
          {
            countFields: 0,
            countResults: 0,
          },
        );
        return {
          id: group.id,
          label: group.name,
          label2:
            pluralise(countFields, '1 Field', '{x} Fields', 'No Fields') +
            (group.repeatable ? ', Repeatable' : ''),
          value: pluralise(countResults, '1 Result', '{x} Results', ''),
          identifier: group.identifier,
        };
      })
      .filter(filterArrayEmpty);
  }, [fieldSettings.fields, fieldSettings.groups, groupIds, resultsCount]);

  const usageByOrganisation = React.useMemo<
    {
      countResults: number;
      id: string;
      name: string;
    }[]
  >(
    () =>
      [
        { id: organisationId, name: 'This Organisation' },
        ...orderBy(section.usedByOrganisations, 'name'),
      ].map((usedByOrganisation) => ({
        id: usedByOrganisation.id,
        name: usedByOrganisation.name,
        countResults: section.groupIds.reduce(
          (acc, groupId) =>
            (fieldSettings.groups[groupId]?.fieldIds || []).reduce(
              (acc1, fieldId) => {
                const field = fieldSettings.fields[fieldId];
                if (!field) {
                  return acc1;
                }
                if (
                  field.type === FieldTypeKey.grouplight &&
                  field.childGroupId
                ) {
                  return (
                    fieldSettings.groups[field.childGroupId]?.fieldIds || []
                  ).reduce(
                    (acc2, childFieldId) =>
                      acc2 +
                      (resultsCount?.[childFieldId]?.[usedByOrganisation.id] ||
                        0),
                    acc1,
                  );
                }
                return (
                  acc1 + (resultsCount?.[fieldId]?.[usedByOrganisation.id] || 0)
                );
              },
              acc,
            ),
          0,
        ),
      })),
    [
      organisationId,
      resultsCount,
      section.usedByOrganisations,
      section.groupIds,
      fieldSettings.groups,
      fieldSettings.fields,
    ],
  );

  const usedByOrganisationIds = section.usedByOrganisations.map((o) => o.id);

  const clientsToAddTo: {
    id: string;
    name: string;
  }[] = orderBy(
    fieldSettings.clients.filter((c) => !usedByOrganisationIds.includes(c.id)),
    'name',
  );

  const handleLinkToOrganisation = () => {
    if (!sectionId) {
      handleSave((savedSectionId) => {
        dispatch(
          NAVIGATE_MODAL({
            screen: ModalScreens.FieldSettingsSectionLinkCustomer,
            params: {
              sectionId: savedSectionId,
              sectionName: dataRef.current.name,
              organisations: clientsToAddTo,
            },
          }),
        );
      });
      return;
    }
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.FieldSettingsSectionLinkCustomer,
        params: {
          sectionId,
          sectionName: dataRef.current.name,
          organisations: clientsToAddTo,
        },
      }),
    );
  };

  const handleUnlinkClient = React.useCallback(
    (orgId: string) => {
      const usage = usageByOrganisation.find((org) => org.id === orgId);
      confirm({
        destructive: true,
        confirmText: 'Unlink',
        title: `Unlink for ${usage?.name}`,
        text:
          'This section will remain available to this organisation but changes you make here will not affect them.\n\n' +
          'No groups, fields, or results will be deleted.\n\n' +
          'This cannot be undone',
        onConfirm: () =>
          dispatch(
            doFieldSettingUnlinkCustomer({
              clientId: orgId,
              id: sectionId!,
            }),
          ),
      });
    },
    [confirm, dispatch, sectionId, usageByOrganisation],
  );

  const handleDeleteClient = React.useCallback(
    (orgId: string) => {
      const usage = usageByOrganisation.find((org) => org.id === orgId)!;
      confirm({
        destructive: true,
        confirmText: 'Delete',
        title: `Delete for ${usage.name}`,
        text:
          'This section will be deleted for this organisation.\n\n' +
          'All groups, fields, and results will be deleted.\n\n' +
          'This cannot be undone',
        onConfirm: () =>
          dispatch(
            doFieldSettingsDeleteCustomer({
              clientId: orgId,
              id: sectionId!,
            }),
          ),
      });
    },
    [confirm, dispatch, sectionId, usageByOrganisation],
  );

  // Navigate back when id given but no state.section.id; the section was deleted
  React.useEffect(() => {
    if (sectionId && !section.id) {
      dispatch(NAVIGATE_MODAL_BACK());
    }
  }, [dispatch, sectionId, section.id]);

  const handleClose = () => {
    if (isDirty.current) {
      confirmUnsaved({
        onSave: () => handleSave(() => dispatch(NAVIGATE_MODAL_BACK())),
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  };

  const reorderEventKey = React.useRef(randomKey());

  React.useEffect(() => {
    const events = [
      doAddEvent(doFieldSettingsSectionSaved, (action) => {
        afterSaveRef.current?.(action.payload);
        afterSaveRef.current = undefined;
        setResetDataI((i) => i + 1);
      }),
      doAddEvent(eventReorderScreenDone, (action) => {
        if (action.payload.key === reorderEventKey.current) {
          dataRef.current.groupIds = action.payload.stringValues;
          forceUpdate();
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
      dispatch(doFieldSettingsSetSectionId(undefined));
    };
  }, [dispatch, forceUpdate, setResetDataI]);

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading={sectionId ? 'Section' : 'Add Section'}
          onPressCancelButton={handleClose}
          cancelButtonText="Close"
          onPressSaveButton={
            editable
              ? () => handleSave(() => dispatch(NAVIGATE_MODAL_BACK()))
              : undefined
          }
        />
      }
    >
      <Card
        explanation={
          section.managedByOrganisation
            ? `This section is managed by ${section.managedByOrganisation.name} and cannot be modified`
            : ''
        }
      >
        {editable ? (
          <TextField
            value={dataRef.current.name}
            name="name"
            placeholder={section.name || 'Enter a name for this section'}
            onChangeWithName={handleChange}
            autoCapitalize="words"
            onEsc={handleClose}
            autoFocusWeb
            autoFocusNative={!sectionId}
            autoFocusDelay
          />
        ) : (
          <CardRow label={dataRef.current.name} />
        )}
      </Card>

      {!!navigationRowGroups.length && (
        <Card
          label="Groups"
          style={{ marginBottom: theme.paddingHorizontalHalf }}
          labelLinkText="Reorder"
          onLabelLinkPress={
            groupIds.length > 1
              ? () =>
                  dispatch(
                    NAVIGATE_MODAL({
                      screen: ModalScreens.REORDER,
                      params: {
                        title: 'Reorder Groups',
                        options: groupIds.reduce<
                          { value: string; name: string }[]
                        >((acc, groupId) => {
                          const group = fieldSettings.groups[groupId];
                          if (group) {
                            acc.push({
                              value: group.id,
                              name: group.name,
                            });
                          }
                          return acc;
                        }, []),
                        eventKey: reorderEventKey.current,
                      },
                    }),
                  )
              : undefined
          }
        >
          {navigationRowGroups.map((group) => (
            <CardRow
              key={group.id}
              onPress={handlePressGroup(group.id)}
              label={group.label}
              value={group.value}
              labelAlignTop
            >
              <Txt size="xsmall">
                {!!group.identifier && (
                  <Txt size="xsmall" style={{ color: theme.colors.support }}>
                    {group.identifier}
                  </Txt>
                )}
                {!!group.identifier && !!group.label2 && ' • '}
                {!!group.label2 && <Txt size="xsmall">{group.label2}</Txt>}
              </Txt>
            </CardRow>
          ))}
        </Card>
      )}

      {editable ? (
        <Card label={navigationRowGroups.length ? '' : 'Groups'}>
          <CardRow
            label="Add group..."
            labelColor={theme.colors.blue}
            icon={ICONS.PlusLight}
            iconColor={theme.colors.blue}
            showMenuOnPress
            hideMenuButton
            onPress={!canSave() ? showCannotSaveAlert : undefined}
            menuOptions={
              canSave()
                ? [
                    {
                      text: 'Standard Group',
                      onPress: () => handleAddGroup(false),
                    },
                    {
                      text: 'Repeatable Group',
                      subText:
                        'Use a repeatable to enter multiples of the same item, e.g. Fire Doors',
                      onPress: () => handleAddGroup(true),
                    },
                  ]
                : undefined
            }
          />
        </Card>
      ) : !navigationRowGroups.length ? (
        <Card label="Fields">
          <CardRow value="This section has no groups" />
        </Card>
      ) : (
        <View
          style={{
            height: theme.paddingVertical - theme.paddingHorizontalHalf,
          }}
        />
      )}

      <Card
        label={pluralise(
          usageByOrganisation,
          '1 Organisation',
          '{x} Organisations',
        )}
        style={{ marginBottom: theme.paddingHorizontalHalf }}
      >
        {showOrganisationUsage ? (
          usageByOrganisation.map((organisation) => (
            <CardRow
              key={organisation.id}
              label={organisation.name}
              value={
                !resultsCount
                  ? ''
                  : pluralise(
                      organisation.countResults,
                      '1 Result',
                      '{x} Results',
                      '',
                    )
              }
              onAction={
                editable && organisation.id !== organisationId
                  ? () => handleDeleteClient(organisation.id)
                  : undefined
              }
              actionIcon={ICONS.TrashCanLight}
              onAction2={
                editable && organisation.id !== organisationId
                  ? () => handleUnlinkClient(organisation.id)
                  : undefined
              }
              actionIcon2={ICONS.LinkHorizontalSlashLight}
            />
          ))
        ) : (
          <CardRow
            label={pluralise(
              usageByOrganisation,
              '1 Organisation',
              '{x} Organisations',
              'No Organisations',
            )}
            value="Tap to see usage..."
            onPress={() => setShowOrganisationUsage(true)}
          />
        )}
      </Card>

      {editable && !!clientsToAddTo.length ? (
        <Card>
          <CardRow
            label="Link to another organisation..."
            labelColor={theme.colors.blue}
            onPress={handleLinkToOrganisation}
            onAdd={handleLinkToOrganisation}
          />
        </Card>
      ) : (
        <View
          style={{
            height: theme.paddingVertical - theme.paddingHorizontalHalf,
          }}
        />
      )}

      {editable && !!section.id && (
        <Button
          text="Delete Section"
          buttonTheme={ButtonTheme.themeless}
          destructive
          style={{ alignSelf: 'center' }}
          onPress={handleDelete}
        />
      )}
    </PageScrollView>
  );
};
